/**
 * 四角色菜單配置 + BaseLayout 類型定義
 * 為 Admin、Manager、Decider、Auditor、Accountant 分別提供菜單和佈局主題配置。
 *
 * Task 8 重構：5 個 getXxxMenuItems 已改為菜單註冊表（menu-registry.ts）的薄封裝，
 * 消費者（AdminLayout / AuditorLayout / CounselorLayout / command-data.ts）保持原有導入路徑，
 * 無需修改任何 import；行為與之前完全一致（userCan 回落至 roles 相容舊 user 物件）。
 *
 * 角色說明：
 * - admin:      系統管理員，全功能存取
 * - manager:    業務管理員，業務功能 + 部分系統設定（差異由能力過濾自動產生）
 * - decider:    驗證決定委員，驗證決定相關功能
 * - auditor:    稽核員，稽核執行相關功能
 * - accountant: 會計，聚焦報價／繳費管理
 */
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';
import { defaultCapabilitiesForRole } from '@zhcb/shared';
import { getMenuForWorkbench } from '@/components/layout/menu-registry';

/* ─── 通用類型定義（保持原導出，供消費者 import） ─── */

/** antd Menu 項目類型 */
export type MenuItem = Required<MenuProps>['items'][number];

/** 側邊欄主題配置 */
export interface SiderConfig {
  /** 側邊欄寬度 (px) */
  width: number;
  /** 背景色 */
  background: string;
  /** 右側邊框 */
  borderRight?: string;
  /** 額外 className（如 'sidebar-dark' 觸發全局 CSS） */
  className?: string;
}

/** Logo 區域配置 */
export interface LogoConfig {
  /** Logo 旁顯示的標題 */
  title: string;
  /** 標題文字顏色 */
  titleColor: string;
  /** Logo 區域背景色 */
  backgroundColor?: string;
  /** Logo icon 漸變色 (CSS gradient) */
  gradient: string;
  /** 副標題（顯示在標題下方） */
  subtitle?: string;
  /** 副標題文字顏色 */
  subtitleColor?: string;
  /** 是否顯示 Logo icon（預設 true） */
  showIcon?: boolean;
}

/** BaseLayout 完整配置接口 */
export interface BaseLayoutConfig {
  /** 側邊欄配置 */
  sider: SiderConfig;
  /** Logo 區域配置 */
  logo: LogoConfig;
  /** 菜單項數組 */
  menuItems: MenuItem[];
  /** 頂欄左側插槽（搜索框等），位於 collapse 按鈕右側 */
  headerLeftExtra?: ReactNode;
  /** 側邊欄菜單上方插槽 */
  siderTopExtra?: ReactNode;
  /** 側邊欄底部插槽 */
  siderBottomExtra?: ReactNode;
}

/* ─── 通用函數 ─── */

/**
 * 跨路徑分組映射表
 * 當某個路徑前綴不直接對應菜單 key 時，通過此映射找到對應的父分組
 */
const CROSS_PATH_GROUP_MAP: Record<string, string> = {
  '/certificate-changes/': 'certificates',
  '/audit-reports/': 'issues-reports',
};

/**
 * 根據當前路徑找到對應的選中菜單項 key（高亮 active 樣式）
 *
 * 策略：在所有路由型菜單 key（以 `/` 開頭）中做「最長前綴匹配」。
 * 匹配規則：`pathname === key` 或 `pathname` 以 `key + '/'` 開頭（避免 `/foo` 誤命中 `/foo-bar`）。
 *
 * 例：`/dispatch/7` → 匹配 `/dispatch`；`/audit/orgs/123` → 匹配 `/audit/orgs`。
 * 若無任何菜單 key 是當前路徑前綴，回退為 `[pathname]`（保持原行為）。
 */
export function findSelectedKeys(pathname: string, menuItems: MenuItem[]): string[] {
  const candidates: string[] = [];

  const walk = (items: MenuItem[]) => {
    for (const item of items) {
      if (!item || !('key' in item)) continue;
      if ('children' in item && item.children) {
        walk(item.children);
        continue;
      }
      const key = item.key;
      if (typeof key !== 'string' || !key.startsWith('/')) continue;
      if (pathname === key || pathname.startsWith(`${key}/`)) {
        candidates.push(key);
      }
    }
  };

  walk(menuItems);

  if (candidates.length === 0) return [pathname];

  /* 取最長前綴（更精確的子路徑優先） */
  candidates.sort((a, b) => b.length - a.length);
  return [candidates[0] ?? pathname];
}

/**
 * 根據路徑找到對應的展開子菜單 key
 * 支持跨路徑分組（如 /certificate-changes/* 歸入 certificates 分組）
 * 支持動態路由（如 /audit-tasks/xxx 歸入 audit-tasks 分組）
 */
export function findOpenKeys(pathname: string, menuItems: MenuItem[]): string[] {
  /* 先嘗試直接匹配子菜單的 key */
  for (const item of menuItems) {
    if (item && 'children' in item && item.children) {
      for (const child of item.children) {
        if (child && 'key' in child && pathname.startsWith(child.key as string)) {
          return [item.key as string];
        }
      }
    }
  }

  /* 跨路徑分組映射回退 */
  for (const [prefix, groupKey] of Object.entries(CROSS_PATH_GROUP_MAP)) {
    if (pathname.startsWith(prefix)) return [groupKey];
  }

  /* 動態路由回退 */
  if (pathname.startsWith('/audit/')) return ['audit-zone'];
  if (pathname.startsWith('/audit-zone/')) return ['audit-zone'];
  if (pathname.startsWith('/audit-tasks/')) return ['audit-zone'];
  if (pathname.startsWith('/dispatch/')) return ['audit-zone'];
  if (pathname.startsWith('/auditor/cases/')) return ['audit-execution'];
  /* /settings/clients 歸入稽核專區（與組織客戶檔案同義），其餘 /settings/* 走系統設置 */
  if (pathname === '/settings/clients' || pathname.startsWith('/settings/clients/')) {
    return ['audit-zone'];
  }
  if (pathname.startsWith('/settings/')) {
    return ['system-settings'];
  }
  if (pathname.startsWith('/non-conformities/')) return ['issues-reports'];
  if (pathname.startsWith('/certificates/')) return ['certificates'];
  if (pathname.startsWith('/analytics')) return ['analytics-group'];
  if (pathname.startsWith('/bulletin/')) return ['bulletin-group'];

  return [];
}

/* ═══════════════════════════════════════════════
 *  薄封裝：5 個 getXxxMenuItems 委託給菜單註冊表
 *
 *  行為保持完全向後相容：
 *  - 舊 user 物件無 capabilities → userCan 回落至 roles 判斷（同舊邏輯）
 *  - admin / manager 共用一棵 admin 型基準樹，差異由 filterMenuTree 自動產生
 * ═══════════════════════════════════════════════ */

/**
 * Admin 菜單項（薄封裝，供命令面板全量目錄使用）。
 *
 * Task 9：AdminLayout 已改為傳入真實 user 物件動態過濾，本封裝僅保留給
 * command-data.ts 作為「全量頁面目錄」用途（包含所有 admin 可訪問的頁面）。
 */
export function getAdminMenuItems(): MenuItem[] {
  return getMenuForWorkbench('admin', { capabilities: defaultCapabilitiesForRole('admin') }, []);
}

/**
 * Manager 菜單項（薄封裝，供命令面板全量目錄使用）。
 *
 * Task 9：AdminLayout 已改為傳入真實 user 物件，由 getMenuForWorkbench 動態過濾，
 * hiddenKeys 由後端 /me 攜帶的 DB 黑名單（role_menu_hidden）提供，本封裝不再注入硬編 hiddenKeys。
 * 命令面板（command-data.ts）以「全量頁面目錄」為目的調用，故保留 manager 預設能力展開、
 * 不傳 hiddenKeys（確保所有 manager 可訪問的頁面均出現在命令面板候選清單中）。
 */
export function getManagerMenuItems(): MenuItem[] {
  return getMenuForWorkbench(
    'manager',
    { capabilities: defaultCapabilitiesForRole('manager') },
    [],
  );
}

/**
 * Accountant 菜單項（薄封裝）。
 * 聚焦報價／繳費，全恆亮，不需能力過濾。
 */
export function getAccountantMenuItems(): MenuItem[] {
  return getMenuForWorkbench('accountant', {}, []);
}

/**
 * Decider 菜單項（薄封裝）。
 * 驗證決定委員，以驗證決定為核心，全恆亮。
 */
export function getDeciderMenuItems(): MenuItem[] {
  return getMenuForWorkbench('decider', {}, []);
}

/**
 * Auditor 菜單項（薄封裝）。
 *
 * Task 8 重構：原 `roles` 參數（用於判斷 decision.vote）已改為菜單樹的
 * `requiredCapability: 'decision.vote'` 標注，通過 `user` 對象的能力集判斷。
 *
 * @param user 當前用戶（含可選 capabilities/roles）。
 *   - 傳入 capabilities → 動態 RBAC 路徑。
 *   - 傳入 roles（無 capabilities）→ 回落至 DEFAULT_ROLE_CAPABILITIES 判斷（兼容舊代碼）。
 *   - 不傳（命令面板全量取菜單）→ 不顯示 decision.vote 受保護項。
 */
export function getAuditorMenuItems(user?: {
  capabilities?: readonly string[];
  roles?: readonly string[];
}): MenuItem[] {
  return getMenuForWorkbench('auditor', user ?? {}, []);
}
